import { type FC, useEffect, useState } from "react"
import { Navigate, useParams } from "react-router"
import styled from "styled-components"

import { type PrivateSession } from "@forento/core/models/privateSession"
import { normalizeHttpUrl } from "@forento/core/utilities/httpUrl"
import { parseNumber } from "@forento/core/utilities/number"
import { useFile } from "@forento/ui/hooks/useFile"

import ImageUploader from "~/components/ImageUploader"
import InputField, { InputTextArea, useRichTextArea } from "~/components/InputField"
import InputLabel from "~/components/InputLabel"
import Layout, { PageBreadcrumb, PageHeader } from "~/components/Layout"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import ProductPricing from "~/components/ProductPricing"
import ZapierId from "~/components/ZapierId"
import { useAlert } from "~/contexts/AlertContext"
import { useToast } from "~/contexts/ToastContext"
import useExternalBuyLink from "~/hooks/useExternalBuyLink"
import usePrice from "~/hooks/usePrice"
import routes from "~/utilities/routes"
import trpc, { query } from "~/utilities/trpc"

const EditSessionPage: FC = () => {
	const sessionId = parseNumber(useParams().sessionId)

	const { data: session, error, refetch } = query.privateSession.get.useQuery(sessionId ?? -1)

	if (session === null) return <Navigate to={routes.sessions.index()} />

	return (
		<Layout>
			<PageHeader>
				<PageBreadcrumb
					title={session?.title ?? "..."}
					path={[{ title: "Sessions", link: routes.sessions.index() }]}
				/>
				{session && <ZapierId id={session.id} />}
			</PageHeader>
			{error ? (
				<p>Failed to load session.</p>
			) : session === undefined ? (
				<PartialLoadingPage />
			) : (
				<Content session={session} reload={refetch} />
			)}
		</Layout>
	)
}

const Content: FC<{ session: PrivateSession; reload(): Promise<unknown> }> = ({ session, reload }) => {
	const toast = useToast()
	const alert = useAlert()

	const [title, setTitle] = useState(session.title)
	const [shortDescription, setShortDescription] = useState(session.shortDescription ?? "")
	const longDescription = useRichTextArea({ initialValue: session.longDescription, label: "Description" })
	const { price, priceValue, setPrice } = usePrice(session.price?.amount.toString() ?? "")
	const [isManualOnly, setManualOnly] = useState(session.isManualOnly)
	const externalBuyLink = useExternalBuyLink(session.externalBuyLink)
	const [isSignInRequired, setSignInRequired] = useState(session.isSignInRequired)
	const [isBokaderoMemberRequired, setBokaderoMemberRequired] = useState(session.isBokaderoMemberRequired ?? false)
	const [membershipIds, setMembershipIds] = useState(session.membershipIds)
	const [isPaused, setPaused] = useState(session.pauseDate !== null)
	const [redemptionInstructions, setRedemptionInstructions] = useState(session.redemptionInstructions ?? "")
	const [redemptionLink, setRedemptionLink] = useState(session.redemptionLink ?? "")

	const thumbnail = useFile(session.id, session.thumbnailFilePath)

	useEffect(() => {
		const isModified =
			title !== session.title ||
			shortDescription.trim() !== (session.shortDescription ?? "") ||
			longDescription.isModified ||
			thumbnail.isModified ||
			priceValue !== (session.price?.amount ?? null) ||
			isManualOnly !== session.isManualOnly ||
			externalBuyLink.isModified ||
			isSignInRequired !== session.isSignInRequired ||
			isBokaderoMemberRequired !== (session.isBokaderoMemberRequired ?? false) ||
			membershipIds.toSorted().join(",") !== session.membershipIds.toSorted().join(",") ||
			isPaused !== (session.pauseDate !== null) ||
			redemptionInstructions.trim() !== (session.redemptionInstructions ?? "") ||
			redemptionLink.trim() !== (session.redemptionLink ?? "")

		if (isModified) {
			toast.setUnsavedChanges(async () => {
				try {
					if (title.trim().length === 0) {
						await alert.show("Error", "You must fill out all required fields.")
						return false
					}
					if (redemptionInstructions.trim().length === 0 && redemptionLink.trim().length === 0) {
						await alert.show(
							"Error",
							"You must provide either redemption instructions or a redemption link.",
						)
						return false
					}
					const normalizedRedemptionLink = normalizeHttpUrl(redemptionLink)
					if (redemptionLink.trim().length > 0 && normalizedRedemptionLink === null) {
						await alert.show("Error", "The redemption link is not a valid URL.")
						return false
					}
					if (isManualOnly && externalBuyLink.error !== null) {
						await alert.show("Error", externalBuyLink.error)
						return false
					}
					await trpc.privateSession.edit.mutate({
						id: session.id,
						data: {
							title,
							shortDescription,
							longDescription: await longDescription.exportEditorState(),
							thumbnailDataUrl: thumbnail.isModified ? await thumbnail.exportToDataUrl() : undefined,
							price: priceValue,
							isManualOnly,
							externalBuyLink: isManualOnly ? externalBuyLink.value : null,
							isBokaderoMemberRequired,
							isSignInRequired,
							membershipIds,
							isPaused,
							redemptionInstructions:
								redemptionInstructions.trim().length > 0 ? redemptionInstructions : null,
							redemptionLink: normalizedRedemptionLink,
						},
					})
					setRedemptionLink(normalizedRedemptionLink ?? "")
					await reload()
					return true
				} catch (error) {
					console.error(error)
					await alert.show("Error", "Failed to save changes. Please try again later.")
					return false
				}
			})
		} else {
			toast.clearUnsavedChanges()
		}
	}, [
		alert,
		isManualOnly,
		externalBuyLink,
		isSignInRequired,
		longDescription,
		longDescription.isModified,
		isBokaderoMemberRequired,
		membershipIds,
		priceValue,
		isPaused,
		redemptionInstructions,
		redemptionLink,
		reload,
		session.id,
		session.isManualOnly,
		session.isSignInRequired,
		session.isBokaderoMemberRequired,
		session.membershipIds,
		session.price?.amount,
		session.pauseDate,
		session.redemptionInstructions,
		session.redemptionLink,
		session.shortDescription,
		session.title,
		shortDescription,
		thumbnail,
		title,
		toast,
	])

	return (
		<Container>
			<InputField label="Title *" value={title} onChange={setTitle} />
			<InputField label="Short description" value={shortDescription} onChange={setShortDescription} />
			{longDescription.element}
			<ImageUploader label="Thumbnail" recommendedSize="thumbnail" {...thumbnail.imageUploader} />
			<InputTextArea
				label="Redemption instructions"
				value={redemptionInstructions}
				onChange={setRedemptionInstructions}
			/>
			<InputField
				label="Redemption link"
				value={redemptionLink}
				onChange={setRedemptionLink}
				placeholder="Calendar booking, etc."
			/>
			<InputLabel>Pricing</InputLabel>
			<ProductPricing
				type="session"
				price={price}
				setPrice={setPrice}
				isManualOnly={isManualOnly}
				externalBuyLink={externalBuyLink}
				setManualOnly={setManualOnly}
				isSignInRequired={isSignInRequired}
				setSignInRequired={setSignInRequired}
				isBokaderoMemberRequired={isBokaderoMemberRequired}
				setBokaderoMemberRequired={setBokaderoMemberRequired}
				membershipIds={membershipIds}
				setMembershipIds={setMembershipIds}
				isPaused={isPaused}
				setPaused={setPaused}
			/>
		</Container>
	)
}

const Container = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
`

export default EditSessionPage
